import { useSortableTable } from '../../../hooks/useSortableTable';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../../utils/currency';
import AddJobModal from '../../../AddJobModal';
import { navigateWithPreference } from '../../../utils/navigationHelper';
import { apiFetch } from '../../../utils/api';
import PageHeader from '../../../components/PageHeader';
import jobStatuses from '../../../constants/job-statuses';

function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  
  // Sorting
  const { items: sortedJobs, requestSort, getSortIndicator } = useSortableTable(jobs, { key: 'job_number', direction: 'desc' });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState([
    'job_number', 'call_type', 'call_priority', 'response_time', 'zone', 
    'quoted_price', 'site_address', 'status', 'category', 'po_number', 'contact'
  ]);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [statusColors, setStatusColors] = useState({});

  useEffect(() => {
    fetchJobs();
    fetchCustomers();
    fetchStatusColors();
  }, []);

  const fetchStatusColors = async () => {
    try {
      const res = await apiFetch('/api/job-status-colors');
      const data = await res.json();
      setStatusColors(jobStatuses.colourLookup(data));
    } catch (err) {
      console.error('Error fetching status colours:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await apiFetch('/api/customers');
      const data = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await apiFetch('/api/jobs');
      const data = await res.json();
      setJobs(Array.isArray(data) ? data : []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setJobs([]);
      setLoading(false);
    }
  };

  const filteredJobs = sortedJobs.filter(job => {
    const matchesSearch = (job.job_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.site_address || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.site_contact || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job.job_status || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch && jobStatuses.matchesStatusFilter(job.job_status, statusFilter);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Jobs"
        subtitle={`${jobs.length} total`}
        backButton
        onBack={() => navigate('/operations')}
        backTitle="Back to Operations"
        sticky
        actionButton={
          <button 
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm"
          >
            ➕ Add a Job
          </button>
        }
      />

      {/* Search Bar + Column Selector */}
      <div className="max-w-full mx-auto px-4 py-3 flex gap-2">
        <input
          type="text"
          placeholder="🔍 Search jobs..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
        />
        <select
          aria-label="Filter by status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
        >
          <option value="all">All statuses</option>
          {jobStatuses.JOB_STATUSES.map((status) => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
        <div className="relative">
          <button
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
          >
            <span>⚙️</span>
            <span className="hidden sm:inline">Columns</span>
          </button>
          {showColumnSelector && (
            <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Show/Hide Columns</h3>
              {[
                { key: 'job_number', label: 'Job #' },
                { key: 'call_type', label: 'Call Type' },
                { key: 'call_priority', label: 'Priority' },
                { key: 'response_time', label: 'Response Time' },
                { key: 'zone', label: 'Zone' },
                { key: 'quoted_price', label: 'Price' },
                { key: 'site_address', label: 'Site Address' },
                { key: 'status', label: 'Status' },
                { key: 'category', label: 'Category' },
                { key: 'po_number', label: 'PO Number' },
                { key: 'contact', label: 'Contact' }
              ].map(col => (
                <label key={col.key} className="flex items-center gap-2 py-1 hover:bg-gray-50 px-2 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={visibleColumns.includes(col.key)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setVisibleColumns([...visibleColumns, col.key]);
                      } else {
                        setVisibleColumns(visibleColumns.filter(c => c !== col.key));
                      }
                    }}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700">{col.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Jobs Table */}
      <div className="max-w-full mx-auto px-6 pb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 ">
          <div className="overflow-auto max-h-[calc(100vh-240px)] relative"><table className="w-full">
            <thead className="bg-gray-300 sticky top-0 z-20 border-b border-gray-400">
              <tr>
                {visibleColumns.includes('job_number') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('job_number')}>Job #{getSortIndicator('job_number')}</th>}
                {visibleColumns.includes('call_type') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('call_type')}>Call Type{getSortIndicator('call_type')}</th>}
                {visibleColumns.includes('call_priority') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('call_priority')}>Priority{getSortIndicator('call_priority')}</th>}
                {visibleColumns.includes('response_time') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('response_time')}>Response Time{getSortIndicator('response_time')}</th>}
                {visibleColumns.includes('zone') && <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('zone')}>Zone{getSortIndicator('zone')}</th>}
                {visibleColumns.includes('quoted_price') && <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('quoted_price')}>Price{getSortIndicator('quoted_price')}</th>}
                {visibleColumns.includes('site_address') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('site_address')}>Site Address{getSortIndicator('site_address')}</th>}
                {visibleColumns.includes('status') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('status')}>Status{getSortIndicator('status')}</th>}
                {visibleColumns.includes('category') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('category')}>Category{getSortIndicator('category')}</th>}
                {visibleColumns.includes('po_number') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('po_number')}>PO{getSortIndicator('po_number')}</th>}
                {visibleColumns.includes('contact') && <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 uppercase cursor-pointer hover:bg-gray-400 select-none" onClick={() => requestSort('contact')}>Contact{getSortIndicator('contact')}</th>}
                <th className="text-right px-3 py-2 text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.map((job, index) => (
                <tr
                  key={job.id}
                  onClick={() => navigateWithPreference(navigate, `/jobs/${job.id}`, 'jobs')}
                  className={`hover:bg-blue-200 cursor-pointer transition border-b border-gray-100 ${
                    index % 2 === 1 ? 'bg-blue-100' : 'bg-white'
                  }`}
                >
                  {visibleColumns.includes('job_number') && (
                    <td className="px-3 py-2">
                      <span className="font-mono text-sm font-semibold text-gray-900">{job.job_number}</span>
                    </td>
                  )}
                  {visibleColumns.includes('call_type') && (
                    <td className="px-3 py-2 text-sm text-gray-900">{job.call_type || '—'}</td>
                  )}
                  {visibleColumns.includes('call_priority') && (
                    <td className="px-3 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        job.call_priority === 'Emergency' ? 'bg-red-100 text-red-700' :
                        job.call_priority === 'Urgent' ? 'bg-blue-100 text-orange-700' :
                        job.call_priority === 'Standard' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {job.call_priority || '—'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.includes('response_time') && (
                    <td className="px-3 py-2 text-sm text-gray-600">{job.response_time || '—'}</td>
                  )}
                  {visibleColumns.includes('zone') && (
                    <td className="px-3 py-2 text-center">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-orange-700 text-xs font-bold">
                        {job.zone || '—'}
                      </span>
                    </td>
                  )}
                  {visibleColumns.includes('quoted_price') && (
                    <td className="px-3 py-2 text-right text-sm font-semibold text-green-700">
                      {job.quoted_price ? `£${parseFloat(job.quoted_price).toFixed(2)}` : '—'}
                    </td>
                  )}
                  {visibleColumns.includes('site_address') && (
                    <td className="px-3 py-2 text-sm text-gray-900">{job.site_address || '—'}</td>
                  )}
                  {visibleColumns.includes('status') && (
                    <td className="px-3 py-2">
                      {(() => {
                        const badge = jobStatuses.appearance(job.job_status, statusColors);
                        return (
                          <span
                            data-status={badge.status}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${badge.badgeClass}`}
                          >
                            <span aria-hidden="true">{badge.icon}</span>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </td>
                  )}
                  {visibleColumns.includes('category') && (
                    <td className="px-3 py-2 text-sm text-gray-600">{job.job_category || '—'}</td>
                  )}
                  {visibleColumns.includes('po_number') && (
                    <td className="px-3 py-2 text-sm text-gray-600">{job.po_number || '—'}</td>
                  )}
                  {visibleColumns.includes('contact') && (
                    <td className="px-3 py-2 text-sm text-gray-600">{job.site_contact || '—'}</td>
                  )}
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/jobs/${job.id}`);
                      }}
                      className="text-orange-600 hover:text-orange-800 text-xs font-medium"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        <div className="mt-2 text-xs text-gray-600">
          Showing {filteredJobs.length} of {jobs.length}
        </div>

        <div>
          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'Try a different search term' : 'Create your first job to get started'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add Job Modal - with customer selector */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Create New Job</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedCustomerId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6">
              {!selectedCustomerId ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Customer *
                  </label>
                  <select
                    value={selectedCustomerId || ''}
                    onChange={(e) => setSelectedCustomerId(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Choose a customer...</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </option>
                    ))}
                  </select>
                  <p className="text-sm text-gray-500 mt-2">
                    Select a customer to continue creating the job
                  </p>
                </div>
              ) : (
                <AddJobModal
                  customerId={selectedCustomerId}
                  customerName={customers.find(c => c.id === selectedCustomerId)?.company_name}
                  onClose={() => {
                    setShowAddModal(false);
                    setSelectedCustomerId(null);
                  }}
                  onJobCreated={(newJob) => {
                    setJobs([newJob, ...jobs]);
                    setShowAddModal(false);
                    setSelectedCustomerId(null);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Jobs;
